import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Star, MessageSquare, BarChart3, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

function Stars({ count }) {
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <Star key={s} className={`h-3.5 w-3.5 ${s <= count ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
      ))}
    </span>
  );
}

function WordCloud({ words }) {
  if (!words || words.length === 0) return <p className="text-sm text-muted-foreground text-center py-8">Noch keine Schlagwörter vorhanden</p>;
  const maxVal = Math.max(...words.map(w => w.value));
  const colors = [
    'text-slate-800', 'text-blue-700', 'text-emerald-700',
    'text-amber-700', 'text-purple-700', 'text-red-700',
    'text-teal-700', 'text-indigo-700', 'text-pink-700',
  ];
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 py-4" data-testid="word-cloud">
      {words.map((w, i) => {
        const ratio = w.value / maxVal;
        const fontSize = Math.max(12, Math.round(ratio * 28 + 10));
        const fontWeight = ratio > 0.5 ? 700 : ratio > 0.25 ? 600 : 400;
        const opacity = Math.max(0.5, ratio);
        return (
          <span
            key={w.text}
            className={`${colors[i % colors.length]} transition-transform hover:scale-110 cursor-default`}
            style={{ fontSize: `${fontSize}px`, fontWeight, opacity }}
            title={`${w.text}: ${w.value}x`}
            data-testid={`word-${w.text}`}
          >
            {w.text}
          </span>
        );
      })}
    </div>
  );
}

function DistributionBar({ data, label }) {
  if (!data || data.length === 0) return null;
  const total = data.reduce((a, b) => a + b, 0);
  const colors = ['bg-red-400', 'bg-amber-400', 'bg-yellow-400', 'bg-lime-400', 'bg-emerald-400'];
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="flex h-5 rounded-full overflow-hidden bg-slate-100" data-testid={`dist-${label.replace(/\s/g, '-').toLowerCase()}`}>
        {data.map((count, i) => {
          const pct = total > 0 ? (count / total) * 100 : 0;
          if (pct === 0) return null;
          return (
            <div key={i} className={`${colors[i]} relative group`} style={{ width: `${pct}%` }}>
              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white opacity-0 group-hover:opacity-100">
                {count}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground px-1">
        <span>1 Stern</span><span>5 Sterne</span>
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' +
      d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
}

export default function AdminSurveys() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/admin/surveys/stats`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => setStats(data))
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="surveys-loading">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!stats || stats.total === 0) {
    return (
      <div className="space-y-6" data-testid="admin-surveys">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Umfragen</h1>
          <p className="text-sm text-muted-foreground">Feedback-Auswertung aus Nutzerumfragen</p>
        </div>
        <Card>
          <CardContent className="py-16 text-center">
            <MessageSquare className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">Noch keine Umfrageergebnisse vorhanden.</p>
            <p className="text-xs text-muted-foreground mt-1">Umfragen werden am Ende jeder Analyse angezeigt.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-surveys">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Umfragen</h1>
        <p className="text-sm text-muted-foreground">{stats.total} Feedback-Antworten ausgewertet</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6 pb-4 px-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-muted-foreground">Antworten gesamt</p>
              <MessageSquare className="h-4 w-4 text-muted-foreground/50" />
            </div>
            <p className="text-3xl font-bold tabular-nums" data-testid="survey-total">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 pb-4 px-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-muted-foreground">Preiskalkulation</p>
              <TrendingUp className="h-4 w-4 text-muted-foreground/50" />
            </div>
            <div className="flex items-center gap-2">
              <p className="text-3xl font-bold tabular-nums" data-testid="avg-price">{stats.avg_stars_price}</p>
              <Stars count={Math.round(stats.avg_stars_price)} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 pb-4 px-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-muted-foreground">Tool-Bewertung</p>
              <BarChart3 className="h-4 w-4 text-muted-foreground/50" />
            </div>
            <div className="flex items-center gap-2">
              <p className="text-3xl font-bold tabular-nums" data-testid="avg-tool">{stats.avg_stars_tool}</p>
              <Stars count={Math.round(stats.avg_stars_tool)} />
            </div>
          </CardContent>
        </Card>
      </div>

      {stats.distribution && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Bewertungsverteilung</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <DistributionBar data={stats.distribution.price} label="Preiskalkulation" />
            <DistributionBar data={stats.distribution.tool} label="Tool-Bewertung" />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Schlagwortwolke</CardTitle>
        </CardHeader>
        <CardContent>
          <WordCloud words={stats.tag_cloud} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Alle Antworten ({stats.responses.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Datum</TableHead>
                  <TableHead className="text-center">Preis</TableHead>
                  <TableHead className="text-center">Tool</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Verbesserungsvorschlag</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.responses.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(r.created_at)}</TableCell>
                    <TableCell className="text-center"><Stars count={r.stars_price} /></TableCell>
                    <TableCell className="text-center"><Stars count={r.stars_tool} /></TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {(r.tags || []).map((t, j) => (
                          <Badge key={j} variant="secondary" className="text-[10px]">{t}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs max-w-[300px] truncate" title={r.improvement_text}>{r.improvement_text || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
